import type React from 'react';
import { useEffect } from 'react';
import { useElementPosition } from './ElementPositionProvider.tsx';
import { useTracking } from '#src/hooks/tracking/TrackingProvider.tsx';

export const GlobalButtonTracker = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { trackLink } = useTracking();
  const elementPosition = useElementPosition();

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent | KeyboardEvent) => {
      const target = event.target as HTMLElement;

      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'SPAN' ||
        target.getAttribute('role') === 'button' ||
        target.getAttribute('role') === 'menuitem'
      ) {
        const positionFromElement =
          target.getAttribute('data-position') || elementPosition;
        const actionFromElement =
          target.getAttribute('data-action') ||
          target.getAttribute('type') ||
          'click';
        const elementText =
          target.innerText ||
          target.getAttribute('aria-label') ||
          target.getAttribute('alt') ||
          'icon';

        const elementDesign =
          { BUTTON: 'button', SPAN: 'span' }[target.tagName] ||
          target.tagName.toLowerCase();

        trackLink({
          eventName: 'developer_site_element_pressed',
          data: {
            elementAction: actionFromElement,
            elementText,
            elementPosition: positionFromElement,
            elementDesign,
          },
        });
      }
    };

    const handleSelectChange = (event: Event) => {
      const target = event.target as HTMLSelectElement;

      if (target.tagName === 'SELECT') {
        const selectedOption = target.options[target.selectedIndex];
        const positionFromSelect =
          target.getAttribute('data-position') || elementPosition;
        const actionFromSelect = target.getAttribute('data-action') || 'select';
        const elementText = selectedOption.text;

        trackLink({
          eventName: 'developer_site_element_pressed',
          data: {
            elementAction: actionFromSelect,
            elementText,
            elementPosition: positionFromSelect,
            elementDesign: 'select',
          },
        });
      }
    };

    document.addEventListener('click', handleGlobalClick, true);
    document.addEventListener('change', handleSelectChange, true);

    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('change', handleSelectChange, true);
    };
  }, [trackLink, elementPosition]);

  return <>{children}</>;
};
